'use client'

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigationSnapshot } from '@/hooks/navigation/useNavigationSnapshot'
import { channelsKeys } from '@/hooks/useChannelsQuery'
import { canaisPorUsuario, chatsPorUsuario, type ChatDoUsuario } from '@/lib/navigation/navegacao'
import { listChannels } from '@/services/channels'
import type { ChannelWithStats } from '@/types/channel'

type NomeDeCanal = { db_id: string; name: string }

const NENHUM_CANAL: readonly NomeDeCanal[] = []

// Nível de módulo: referência estável pro `select` não recalcular a cada render.
function nomesDosCanais(lista: ChannelWithStats[]): readonly NomeDeCanal[] {
  return lista.map((c) => ({ db_id: c.db_id, name: c.name }))
}

/** Observer PASSIVO de `['channels']` (`enabled: false`): lê o cache que o rail mantém
 *  vivo, sem buscar, sem polling e sem hidratar/gravar o IndexedDB. O `select` reduz a
 *  `{db_id, name}` e o structural sharing segura a referência quando só contador muda. */
function useNomesDosCanais(): readonly NomeDeCanal[] {
  const { data } = useQuery<ChannelWithStats[], Error, readonly NomeDeCanal[]>({
    queryKey: channelsKeys.all,
    queryFn: () => listChannels(),
    enabled: false,
    select: nomesDosCanais,
  })
  return data ?? NENHUM_CANAL
}

/**
 * `user_id → nomes dos canais abertos`, pra 2ª linha da sidebar de presença.
 * Chamado UMA vez pelo container (drawer, peek), nunca por linha. Nome via
 * `useNomesDosCanais` (cache passivo). Canal fora do cache é omitido. Gate triplo
 * herdado do `useNavigationSnapshot`.
 */
export function useCanaisDosUsuarios(): ReadonlyMap<string, readonly string[]> {
  const { canais } = useNavigationSnapshot()
  const nomes = useNomesDosCanais()
  return useMemo(() => canaisPorUsuario(canais, nomes), [canais, nomes])
}

/** `user_id → chats abertos` (canal + contato), pro hover e o menu da sidebar de presença. Mesmo regime do de cima. */
export function useChatsDosUsuarios(): ReadonlyMap<string, readonly ChatDoUsuario[]> {
  const { canais } = useNavigationSnapshot()
  const nomes = useNomesDosCanais()
  return useMemo(() => chatsPorUsuario(canais, nomes), [canais, nomes])
}
